import { ref } from 'vue'
import { useToast } from './useToast'

export interface ErrorInfo {
  code?: string
  message: string
  details?: any
  timestamp: Date
  userAgent: string
  url: string
}

export const useErrorHandler = () => {
  const { showError } = useToast()
  const errors = ref<ErrorInfo[]>([])

  const handleError = (error: any, context?: string) => {
    const errorInfo: ErrorInfo = {
      code: error.code || error.name || 'UNKNOWN_ERROR',
      message: error.message || '未知錯誤',
      details: error.details || error,
      timestamp: new Date(),
      userAgent: navigator.userAgent,
      url: window.location.href
    }

    // 記錄錯誤
    errors.value.unshift(errorInfo)
    
    // 限制錯誤記錄數量
    if (errors.value.length > 50) {
      errors.value = errors.value.slice(0, 50)
    }

    // 顯示用戶友好的錯誤訊息
    const userMessage = getUserFriendlyMessage(errorInfo.code, errorInfo.message)
    showError(userMessage, { duration: 8000 })

    // 記錄到控制台
    console.error(`[${context || 'Error'}]`, errorInfo)

    // 記錄 GA4 錯誤事件
    if (typeof gtag !== 'undefined') {
      gtag('event', 'exception', {
        description: `${context || 'Error'}: ${errorInfo.message}`,
        fatal: false
      })
    }

    return errorInfo
  }

  const getUserFriendlyMessage = (code?: string, message?: string): string => {
    if (!code) return message || '發生未知錯誤，請稍後再試'

    const errorMessages: Record<string, string> = {
      // Firebase Auth 錯誤
      'auth/user-not-found': '找不到此帳號，請檢查電子信箱或手機號碼',
      'auth/wrong-password': '密碼錯誤，請重新輸入',
      'auth/invalid-email': '電子信箱格式不正確',
      'auth/user-disabled': '此帳號已被停用，請聯絡客服',
      'auth/too-many-requests': '嘗試次數過多，請稍後再試',
      'auth/network-request-failed': '網路連線失敗，請檢查網路設定',
      'auth/weak-password': '密碼強度不足，請使用更強的密碼',
      'auth/email-already-in-use': '此電子信箱已被使用',
      'auth/operation-not-allowed': '此操作不被允許',
      'auth/requires-recent-login': '需要重新登入以完成此操作',

      // Firebase Functions 錯誤
      'functions/not-found': '服務暫時無法使用，請稍後再試',
      'functions/invalid-argument': '輸入資料有誤，請檢查後重新提交',
      'functions/permission-denied': '權限不足，請聯絡管理員',
      'functions/failed-precondition': '操作條件不滿足',
      'functions/aborted': '操作被中止',
      'functions/out-of-range': '數值超出範圍',
      'functions/unimplemented': '功能尚未實作',
      'functions/internal': '系統內部錯誤，請稍後再試',
      'functions/unavailable': '服務暫時無法使用',
      'functions/data-loss': '資料遺失，請重新操作',
      'functions/unauthenticated': '請先登入',
      'functions/resource-exhausted': '系統資源不足',
      'functions/cancelled': '操作已取消',
      'functions/deadline-exceeded': '操作逾時',

      // Firestore 錯誤
      'firestore/permission-denied': '沒有權限存取此資料',
      'firestore/not-found': '找不到相關資料',
      'firestore/already-exists': '資料已存在',
      'firestore/resource-exhausted': '系統資源不足',
      'firestore/failed-precondition': '操作條件不滿足',
      'firestore/aborted': '操作被中止',
      'firestore/out-of-range': '數值超出範圍',
      'firestore/unimplemented': '功能尚未實作',
      'firestore/internal': '系統內部錯誤',
      'firestore/unavailable': '服務暫時無法使用',
      'firestore/data-loss': '資料遺失',
      'firestore/unauthenticated': '請先登入',
      'firestore/cancelled': '操作已取消',
      'firestore/deadline-exceeded': '操作逾時',

      // 網路錯誤
      'NETWORK_ERROR': '網路連線失敗，請檢查網路設定',
      'TIMEOUT_ERROR': '連線逾時，請稍後再試',
      'SERVER_ERROR': '伺服器錯誤，請稍後再試',

      // 驗證錯誤
      'VALIDATION_ERROR': '資料驗證失敗，請檢查輸入內容',
      'FORM_VALIDATION_ERROR': '表單資料有誤，請檢查後重新提交',

      // 業務邏輯錯誤
      'INVALID_REFERRAL_CODE': '推薦碼無效，請確認連結正確',
      'BOOKING_NOT_FOUND': '找不到相關訂單',
      'AFFILIATE_NOT_FOUND': '找不到相關兼職人員',
      'PRODUCT_NOT_FOUND': '找不到相關產品',
      'INSUFFICIENT_PERMISSIONS': '權限不足',
      'DUPLICATE_ENTRY': '資料重複，請檢查後重新提交',
      'INVALID_STATUS': '狀態無效',
      'PAYMENT_FAILED': '付款失敗',
      'QUOTA_EXCEEDED': '配額已滿',

      // 系統錯誤
      'SYSTEM_ERROR': '系統錯誤，請稍後再試',
      'MAINTENANCE_MODE': '系統維護中，請稍後再試',
      'RATE_LIMIT_EXCEEDED': '請求過於頻繁，請稍後再試'
    }

    return errorMessages[code] || message || '發生未知錯誤，請稍後再試'
  }

  const clearErrors = () => {
    errors.value = []
  }

  const getRecentErrors = (limit = 10) => {
    return errors.value.slice(0, limit)
  }

  const reportError = async (_error: ErrorInfo, _additionalInfo?: any) => {
    // No remote error-reporting service is configured. Intentionally avoid
    // logging payloads because they can contain customer or account data.
  }

  return {
    errors,
    handleError,
    getUserFriendlyMessage,
    clearErrors,
    getRecentErrors,
    reportError
  }
}

// 全域錯誤處理器
export const setupGlobalErrorHandler = () => {
  const { handleError } = useErrorHandler()

  // 捕獲未處理的 Promise 拒絕
  window.addEventListener('unhandledrejection', (event) => {
    handleError(event.reason, 'Unhandled Promise Rejection')
  })

  // 捕獲未處理的 JavaScript 錯誤
  window.addEventListener('error', (event) => {
    handleError(event.error || event.message, 'JavaScript Error')
  })

  // Vue 錯誤處理器
  const app = (window as any).__VUE_APP__
  if (app) {
    app.config.errorHandler = (error: any, instance: any, info: string) => {
      handleError(error, `Vue Error: ${info}`)
    }
  }
}









